import Service, { service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { convertToLocalDatetimeNoSeconds } from 'my-phorest/utils/local-datetime-helpers';
import {
  isAfter,
  isBetween,
  parse,
  addTime,
} from 'my-phorest/utils/local-time-helpers';

export default class CalendarSlotHoverService extends Service {
  @tracked isPopoverOpen = false;
  @tracked show = false;
  @tracked time;
  @tracked slotReference;
  @tracked appendSlotTo;

  @service fullCalendar;
  @service intl;

  @action
  handleMove(e) {
    this.handleMouseMove(e);
  }

  @action
  removeHoverEvent() {
    if (!this.isBlocked) {
      this.show = false;
    }
  }

  @action
  handleMouseMove(mouseEvent) {
    if (this.isBlocked) {
      return;
    }
    this.show = false;

    const { x, y } = mouseEvent;
    const allElements = document.elementsFromPoint(x, y);

    const element = allElements.find((el) =>
      el.classList.contains('fc-timegrid-slot-lane')
    );

    if (element) {
      if (this._hideHoverWhenIsAppointment(allElements)) {
        return;
      }
      this.#updateHoverTime(element, mouseEvent);
    }
  }

  get isBlocked() {
    return this.isPopoverOpen || this._isBlocked;
  }

  set isBlocked(value) {
    this._isBlocked = value;
  }

  get calendarApi() {
    return this.fullCalendar.calendarApi;
  }

  get slotDateTime() {
    return convertToLocalDatetimeNoSeconds(`${this.getDate()}T${this.time}`);
  }

  getDate() {
    return this.slotReference?.col?.attributes['data-date']?.value;
  }

  getResource() {
    let id = this.slotReference?.col?.attributes['data-resource-id']?.value;
    return id && this.calendarApi.getResourceById(id);
  }

  _getTime(el, $col) {
    if (this._isRowLaneElement(el)) {
      let time = el.attributes['data-time']?.value;
      return this.#adjustOverlapAppointmentTime($col, time);
    }
  }

  _isRowLaneElement(el) {
    return el && el.classList.contains('fc-timegrid-slot-lane');
  }

  _getHoverColumn(element, event) {
    let cols = document.querySelectorAll('.fc-col-header thead th');
    let columnsCount = cols.length;
    let columnWidth = element.clientWidth / columnsCount;
    let rect = element.getBoundingClientRect();
    let index = Math.floor((event.x - rect.x) / columnWidth);
    return cols.item(index);
  }

  _hideHoverWhenIsAppointment(elements) {
    if (
      elements.find((el) => el.classList.contains('fc-timegrid-event-harness'))
    ) {
      this.removeHoverEvent();
      return true;
    }

    return false;
  }

  #getRelevantEvents($column) {
    if (this.fullCalendar.isWeekView) {
      let date = $column.getAttribute('data-date');
      return this.fullCalendar.calendarApi
        .getEvents()
        .filter((a) => a.extendedProps.date === date);
    } else {
      let staffId = $column.getAttribute('data-resource-id');
      let resource = this.fullCalendar.calendarApi.getResourceById(staffId);
      return resource.getEvents();
    }
  }

  #eventEndsBetweenSlotHover(slotStartTime, slotEndTime) {
    return (event) => {
      let { endTime: eventEndTime } = event.extendedProps;
      let parsed = parse(eventEndTime);
      return (
        isBetween(parsed, {
          start: slotStartTime,
          end: slotEndTime,
        }) && slotEndTime !== parsed
      );
    };
  }

  #overlappingAppointments($column, time) {
    if (!$column) return;
    let events = this.#getRelevantEvents($column);
    let slotDuration = this.fullCalendar.slotDurationMins;
    let slotStartTime = parse(time);
    let slotEndTime = addTime(slotStartTime, slotDuration);

    return events.filter(
      this.#eventEndsBetweenSlotHover(slotStartTime, slotEndTime)
    );
  }

  #adjustOverlapAppointmentTime($column, time) {
    let appointments = this.#overlappingAppointments($column, time);
    if (appointments?.length) {
      let [lastOverLappingAppointment] = appointments.sort((a, b) =>
        isAfter(parse(a.extendedProps.endTime), parse(b.extendedProps.endTime))
          ? -1
          : 1
      );
      return lastOverLappingAppointment.extendedProps.endTime;
    }
    return time;
  }

  #updateHoverTime(row, mouseEvent) {
    let col = this._getHoverColumn(row, mouseEvent);
    let time = this._getTime(row, col);
    if (!time) {
      return;
    }

    this.appendSlotTo = document.querySelector(
      '[data-appointment-calendar-container] tbody .fc-timegrid-body'
    );

    this.slotReference = {
      col,
      row,
    };

    this.time = time;
    this.show = true;
  }
}
